<template>
  <div>
    <!-- 凡例 -->
    <div class="d-flex align-center mb-3 gap-3">
      <div class="d-flex align-center">
        <span
          class="legend-dot"
          style="background: #4caf50"
        />
        <span class="text-caption">来店中</span>
      </div>
      <div class="d-flex align-center">
        <span
          class="legend-dot"
          style="background: #e0e0e0"
        />
        <span class="text-caption">完了</span>
      </div>
      <v-spacer />
      <v-btn
        small
        color="primary"
        outlined
        @click="openNewReservation"
      >
        <v-icon
          small
          left
        >
          {{ icons.mdiPlus }}
        </v-icon>
        予約を追加
      </v-btn>
    </div>

    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <template v-else>
      <!-- 時間帯サマリー（18時台 1組2名 …） -->
      <div
        v-if="slotSummary.length > 0"
        class="d-flex flex-wrap gap-2 mb-3"
      >
        <span
          v-for="slot in slotSummary"
          :key="`slot-${slot.hour}`"
          class="slot-pill"
        >
          {{ slot.label }} <b>{{ slot.groups }}組 {{ slot.guests }}名</b>
        </span>
        <span class="slot-pill slot-pill--muted">卓数 {{ totalTableCount }}</span>
      </div>

      <!-- 時間帯順の予約リスト（行タップ=編集 / 卓作成=ウィザード） -->
      <div
        v-if="sortedReservations.length === 0"
        class="text-body-2 text-medium-emphasis pa-4"
      >
        この日の予約はありません
      </div>
      <div
        v-for="res in sortedReservations"
        :key="`rsv-${res.id}`"
        class="rsv-row"
        @click="onReservationClick(res)"
      >
        <span class="rsv-row__time">{{ timeRange(res) }}</span>
        <span
          v-if="res.attributes.status !== 'confirmed'"
          class="rsv-row__badge"
          :style="{ background: statusColor(res) }"
        >{{ statusLabel(res) }}</span>
        <span class="rsv-row__who">
          {{ customerName(res) }} {{ res.attributes.customerCount }}名
          <span
            v-if="nominationSummary(res)"
            class="rsv-row__sub"
          >{{ nominationSummary(res) }}</span>
          <span
            v-if="courseName(res)"
            class="rsv-row__course"
          >{{ courseName(res) }}</span>
        </span>
        <span
          v-if="missingItemsOf(res).length > 0"
          class="board-warn-chip"
          :title="`卓作成には${missingItemsOf(res).join('・')}の入力が必要です`"
        >
          ⚠ 要{{ missingItemsOf(res).join('・') }}
        </span>
        <span
          v-if="res.attributes.status === 'checked_in'"
          class="rsv-row__linked"
        >
          ✓ 卓作成済み{{ tableLabelOf(res) }}
        </span>
        <v-spacer />
        <span class="rsv-row__edit-hint">
          <v-icon size="14">{{ icons.mdiPencilOutline }}</v-icon>
          編集
        </span>
        <v-btn
          v-if="canCreateTable(res)"
          x-small
          outlined
          color="success"
          :disabled="missingItemsOf(res).length > 0"
          @click.stop="openWizard(res)"
        >
          <v-icon
            x-small
            left
          >
            {{ icons.mdiSofaSingle }}
          </v-icon>
          卓を作成
        </v-btn>
      </div>
    </template>

    <!-- 予約編集ダイアログ -->
    <reservation-dialog
      v-model="dialogOpen"
      :reservation="selectedReservation"
      :casts="casts"
      :courses="courses"
      :referrals="referrals"
      :nominations="nominationMenus"
      :customer-tags="customerTags"
      @saved="onSaved"
      @check-in-requested="onCheckInRequested"
      @error="onError"
    />

    <!-- 予約の新規登録ウィザード（日時・人数 → 指名・コース → 確認） -->
    <reservation-create-wizard
      v-model="createWizardOpen"
      :default-date="date"
      :casts="casts"
      :courses="courses"
      :nominations="nominationMenus"
      :referrals="referrals"
      :customer-tags="customerTags"
      @saved="onSaved"
      @error="onError"
    />

    <!-- 卓作成ウィザード（ホール → 卓 → 確認・修正） -->
    <reservation-check-in-wizard
      v-model="wizardOpen"
      :reservation="wizardReservation"
      :halls="halls"
      :casts="casts"
      :courses="courses"
      :nominations="nominationMenus"
      :day-reservations="reservations"
      @completed="onWizardCompleted"
      @error="onError"
    />

    <!-- エラーSnackbar -->
    <v-snackbar
      v-model="snackbar"
      color="error"
      timeout="4000"
    >
      {{ snackbarMessage }}
    </v-snackbar>

    <!-- 成功Snackbar -->
    <v-snackbar
      v-model="successSnackbar"
      color="success"
      timeout="2000"
    >
      {{ successMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import { mdiSofaSingle, mdiPlus, mdiPencilOutline } from '@mdi/js'
import ReservationApi from '@/api/waiter/Reservation'
import { checkInMissingItems, buildTimeSlotSummary } from '@/views/waiter/reservations/reservationDisplayUtils'
import { getBusinessTodayStr } from '@/views/waiter/floorUtils'
import useCurrentData from '@/views/composable/useCurrentData'
import HallApi from '@/api/waiter/Hall'
import UserApi from '@/api/waiter/User'
import VendibleApi from '@/api/waiter/Vendible'
import ReferralApi from '@/api/waiter/Referral'
import CustomerTagApi from '@/api/waiter/CustomerTag'
import ReservationDialog from './ReservationDialog.vue'
import ReservationCheckInWizard from './ReservationCheckInWizard.vue'
import ReservationCreateWizard from './ReservationCreateWizard.vue'

// 予約管理の中身（凡例・時間帯サマリー・時間帯順リスト・編集ダイアログ・卓作成ウィザード）。
// 予約は時間帯だけで登録し、卓は来店時にウィザードで選ぶ（.steering/20260909-reservation-table-on-checkin/）
export default {
  name: 'ReservationDayBoard',
  components: {
    ReservationDialog,
    ReservationCheckInWizard,
    ReservationCreateWizard,
  },
  props: {
    // 対象日（yyyy-MM-dd）
    date: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      icons: { mdiSofaSingle, mdiPlus, mdiPencilOutline },
      reservations: [],
      halls: [],
      casts: [],
      courses: [],
      nominationMenus: [],
      referrals: [],
      customerTags: [],
      isLoading: false,
      dialogOpen: false,
      selectedReservation: null,
      createWizardOpen: false,
      wizardOpen: false,
      wizardReservation: null,
      snackbar: false,
      snackbarMessage: '',
      successSnackbar: false,
      successMessage: '保存しました',
    }
  },
  computed: {
    // キャンセルを除き、時間帯順（深夜〜5時台は翌側扱いで最後）に並べる
    sortedReservations() {
      const sortKey = res => {
        const match = String(res.attributes?.startTime || '').match(/(\d{2}):(\d{2})/)
        if (!match) return 9999
        const hour = Number(match[1])

        return (hour < 6 ? hour + 24 : hour) * 60 + Number(match[2])
      }

      return this.reservations
        .filter(r => r.attributes?.status !== 'canceled')
        .slice()
        .sort((a, b) => sortKey(a) - sortKey(b))
    },
    slotSummary() {
      return buildTimeSlotSummary(this.sortedReservations.map(r => r.attributes))
    },
    totalTableCount() {
      return this.halls.reduce((sum, h) => sum + (h.attributes?.tableCount || 0), 0)
    },
    // 来店処理（卓作成）は予約日=当営業日のときだけ（サーバ側でも同じ検証あり）
    isBusinessToday() {
      const { currentClub } = useCurrentData()

      return this.date === getBusinessTodayStr(new Date(), currentClub.value?.openAt)
    },
  },
  watch: {
    date() {
      this.fetchReservations()
    },
  },
  created() {
    this.fetchAll()
  },
  methods: {
    // 指名の表示（新形式の複数指名を優先、旧nominatedUserはフォールバック）
    nominationSummary(res) {
      const nominations = res.attributes.nominations || []
      if (nominations.length > 0) {
        return nominations.map(n => `${n.userName}(${n.nominationName})`).join('・')
      }

      return res.attributes.nominatedUser?.name || null
    },
    async fetchAll() {
      this.isLoading = true
      try {
        await Promise.all([
          this.fetchReservations(),
          this.fetchMasterData(),
        ])
      } finally {
        this.isLoading = false
      }
    },
    async fetchReservations() {
      try {
        const { data } = await ReservationApi.getReservations({ date: this.date })
        this.reservations = data?.reservations?.data || data?.data || []
      } catch (e) {
        console.error('[Reservation] fetch failed:', e)
      }
    },
    async fetchMasterData() {
      try {
        const [hallsRes, usersRes, vendiblesRes, referralsRes, tagsRes] = await Promise.all([
          HallApi.getHalls({ isOperation: true }),
          UserApi.getUsers(),
          VendibleApi.getVendibles(),
          ReferralApi.getReferrals(),
          CustomerTagApi.getCustomerTags(),
        ])

        this.halls = hallsRes.data?.halls?.data || []
        const allUsers = usersRes.data?.users?.data || []
        this.casts = allUsers.filter(u => u.attributes?.role === 'cast')
        const allVendibles = vendiblesRes.data?.vendibles?.data || []
        this.courses = allVendibles.filter(v => v.attributes?.vendibleType === 'Course')
        this.nominationMenus = allVendibles.filter(v => v.attributes?.vendibleType === 'Nomination')
        this.referrals = referralsRes.data?.referrals?.data || []
        this.customerTags = tagsRes.data?.customerTags?.data || []
      } catch {
        // マスタ取得失敗は許容
      }
    },
    openNewReservation() {
      this.createWizardOpen = true
    },
    onReservationClick(reservation) {
      this.selectedReservation = reservation
      this.dialogOpen = true
    },
    onSaved() {
      this.successMessage = '保存しました'
      this.successSnackbar = true
      this.fetchReservations()
      this.$emit('changed')
    },
    onError(msg) {
      this.snackbarMessage = msg
      this.snackbar = true
    },
    // 来店処理前に不足している項目（指名あり×コース未選択）
    missingItemsOf(reservation) {
      if (reservation.attributes?.status !== 'confirmed') return []

      return checkInMissingItems(reservation.attributes || {})
    },
    canCreateTable(reservation) {
      return this.isBusinessToday && reservation.attributes?.status === 'confirmed'
    },
    openWizard(reservation) {
      this.wizardReservation = reservation
      // 予約propの反映をウィザードのopenより先に確定させる（openと同時だと
      // 手順3のフォーム初期化が古いpropを読むことがある）
      this.$nextTick(() => {
        this.wizardOpen = true
      })
    },
    // ダイアログの来店処理ボタンもウィザードに集約する（卓を選んでから作成）。
    // reservation はダイアログが保存直後に返す最新のserializer行
    onCheckInRequested(reservation) {
      this.dialogOpen = false
      this.fetchReservations()
      this.openWizard(reservation)
    },
    onWizardCompleted() {
      this.$emit('changed')
      this.goToHalls()
    },
    goToHalls() {
      // 卓作成後はホール(卓一覧)へ遷移
      this.$router.push({
        name: 'waiter-halls',
        params: { club_slug: this.$route.params.club_slug },
      })
    },
    customerName(res) {
      const customers = res.attributes?.customers
      if (!customers || customers.length === 0) return '顧客未設定'

      return customers[0].name + (customers.length > 1 ? ` 他${customers.length - 1}名` : '')
    },
    courseName(res) {
      return res.attributes?.course?.name || null
    },
    tableLabelOf(res) {
      const hall = res.attributes?.hall
      if (!hall) return ''
      const table = res.attributes?.tableNumber ? ` ${res.attributes.tableNumber}卓` : ''

      return `（${hall.name}${table}）`
    },
    timeRange(res) {
      const { startTime, endTime } = res.attributes || {}
      const fmt = t => {
        const match = String(t || '').match(/(\d{2}):(\d{2})/)

        return match ? `${match[1]}:${match[2]}` : ''
      }

      return endTime ? `${fmt(startTime)}〜${fmt(endTime)}` : `${fmt(startTime)}〜`
    },
    statusColor(res) {
      return {
        confirmed: '#1976d2',
        checked_in: '#4caf50',
        completed: '#e0e0e0',
      }[res.attributes?.status] || '#1976d2'
    },
    statusLabel(res) {
      return {
        confirmed: '確定',
        checked_in: '来店中',
        completed: '完了',
        canceled: 'キャンセル',
      }[res.attributes?.status] || ''
    },
  },
}
</script>

<style scoped>
.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 4px;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

/* 時間帯サマリー */
.slot-pill {
  border: 1px solid rgba(58, 53, 65, 0.09);
  background: rgba(145, 85, 253, 0.08);
  border-radius: 8px;
  padding: 4px 12px;
  font-size: 11.5px;
}

.slot-pill b {
  color: #9155fd;
}

.slot-pill--muted {
  background: transparent;
  color: #94a3b8;
}

/* 時間帯順の予約行 */
.rsv-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 6px;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.25);
  flex-wrap: wrap;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.1s;
}

.rsv-row:hover {
  background: rgba(145, 85, 253, 0.08);
}

/* 行タップで編集モーダルが開くことを示す（ホバーが無いタブレットでも見えるよう常時表示） */
.rsv-row__edit-hint {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
}

.rsv-row__course {
  display: inline-block;
  font-size: 11px;
  color: #9155fd;
  border: 1px solid rgba(145, 85, 253, 0.4);
  border-radius: 8px;
  padding: 0 7px;
  margin-left: 4px;
}

.rsv-row__time {
  font-weight: 700;
  min-width: 96px;
  font-variant-numeric: tabular-nums;
}

.rsv-row__badge {
  color: #fff;
  border-radius: 11px;
  padding: 1px 9px;
  font-size: 11px;
  font-weight: 600;
}

.rsv-row__who {
  min-width: 150px;
}

.rsv-row__sub {
  font-size: 11.5px;
  color: #94a3b8;
}

.rsv-row__linked {
  font-size: 11px;
  font-weight: 700;
  color: #2e9e5b;
}

/* 来店処理の必須項目が不足している予約の警告 */
.board-warn-chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  color: #b45309;
  background: rgba(232, 162, 61, 0.12);
  border: 1px solid rgba(232, 162, 61, 0.5);
  border-radius: 8px;
  padding: 0 8px;
}
</style>
